import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { applyStoredRefCode } from '~/utils/referral-storage'
import { resolveSafeRedirectPath } from '~/utils/auth-redirect'

export const Route = createFileRoute('/$locale/oauth/callback')({
  ssr: false,
  validateSearch: (s: Record<string, unknown>) => ({
    session: typeof s.session === 'string' ? s.session : undefined,
    error: typeof s.error === 'string' ? s.error : undefined,
    callbackURL: typeof s.callbackURL === 'string' ? s.callbackURL : undefined,
  }),
  component: OAuthCallback,
})

function OAuthCallback() {
  const { locale } = Route.useParams()
  const { session, error, callbackURL } = Route.useSearch()
  const navigate = useNavigate()

  useEffect(() => {
    if (error || !session) {
      navigate({ to: '/$locale/sign-in', params: { locale }, search: { error: 'oauth_failed' } })
      return
    }

    const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString()
    const domain = import.meta.env.PROD ? '; domain=.xeito.ai' : ''
    const secure = import.meta.env.PROD ? '; Secure' : ''
    document.cookie = `xeito_session=${session}; path=/; expires=${expires}${domain}${secure}; SameSite=Lax`

    // B-new-30 v1c-B — if a referral code was stored on the sign-in / sign-up
    // entry page (see referral-storage.captureRefCodeFromUrl), POST it to the
    // API now that the session cookie is set. Fire-and-forget: never blocks
    // the post-signin redirect. Composes on v1c-A's POST /api/referral/apply
    // (idempotent server-side; safe on retries + double-fire).
    const apiUrl = import.meta.env.VITE_API_URL || ''
    if (apiUrl) {
      void applyStoredRefCode(apiUrl)
    }

    // Redirect to the originally intended page (if valid) or fall back to
    // dashboard. B-new-1129 — a naive `/`-prefix check is not enough: a
    // backslash right after the leading `/` (or a stripped TAB/CR/LF) still
    // satisfies it yet resolves to a FOREIGN origin once the browser
    // actually parses it. `resolveSafeRedirectPath` settles this the way a
    // browser will, by parsing against the real origin — see
    // apps/web/src/utils/auth-redirect.ts.
    // B-new-9220 — the callback cannot know onboarding completion, so with no
    // explicit destination (absent, or only the default dashboard callback)
    // it routes to the gated /welcome landing step, which resolves
    // onboarding vs dashboard once Terms are accepted and the session is
    // usable. Explicit destinations still go straight through.
    const fallback = `/${locale}/dashboard/overview`
    const safeRedirectPath = resolveSafeRedirectPath({
      fromQuery: callbackURL,
      fallback: `/${locale}/welcome`,
    })
    // Defense-in-depth: re-parse against the real origin and re-check
    // before navigating, so a bare relative string can never be handed to
    // `window.location.href` unchecked.
    const destination = new URL(safeRedirectPath, window.location.origin)
    const isDefaultCallback = destination.pathname === fallback
    const safeRedirect = isDefaultCallback
      ? `${window.location.origin}/${locale}/welcome`
      : destination.origin === window.location.origin
        ? destination.href
        : `${window.location.origin}${fallback}`

    // Full page reload so AuthProvider re-mounts and reads the new session cookie
    window.location.href = safeRedirect
  }, [])

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center">
      <p className="text-[#a0a0b0] font-mono text-sm">Signing you in...</p>
    </div>
  )
}
